<script setup lang="ts">
  import { ref } from 'vue';
  import { useContactForm } from '@/composables/useContactForm';
  import FormField from '@/components/form/FormField.vue';
  import FormSelect from '@/components/form/FormSelect.vue';
  import FormCheckbox from '@/components/form/FormCheckbox.vue';
  import type { PropType } from 'vue';
  import { useLegalDocumentStore } from '@/stores/legal-document-store';

  defineProps({
    packageNames: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    selectedPackage: {
      type: String,
      required: false,
      default: undefined
    },
    localePath: {
      type: Function as PropType<(to: any) => string>,
      required: true
    },
    submitLabel: {
      type: String,
      default: 'contact.form.submit'
    }
  });

  const { fields, handleSubmit, context } = useContactForm();

  const emit = defineEmits(['onSubmit']);

  const isSuccess = ref(false);
  const isError = ref(false);

  const onSubmit = handleSubmit((formData: object, { resetForm }) => {
    isSuccess.value = false;
    isError.value = false;

    emit('onSubmit', { ...formData, context: context.value, isSuccess, isError, resetForm });
  });

  const { getDocumentSlugForAlias } = useLegalDocumentStore();
</script>

<template>
  <div class="contact-form">
    <div
      v-if="isSuccess"
      class="form-message form-message--success">
      {{ $t('chat.form.success') }}
    </div>
    <div
      v-if="isError"
      class="form-message form-message--error">
      {{ $t('chat.form.error') }}
    </div>
    <form
      novalidate
      @submit.prevent="onSubmit">
      <div class="contact-form-block">
        <h3 class="contact-form-block__title">{{ $t('contact.form.customerDataBlockTitle') }}</h3>
        <FormField v-bind="fields.name" />
        <FormField v-bind="fields.email" />
        <FormField v-bind="fields.companyName" />
        <div class="grid lg:grid-cols-2 gap-x-4">
          <FormField v-bind="fields.phone" />
          <FormField v-bind="fields.vatNumber" />
        </div>
      </div>
      <div class="contact-form-block">
        <h3 class="contact-form-block__title">{{ $t('contact.form.hqBlockTitle') }}</h3>
        <div class="grid lg:grid-cols-3 gap-x-4">
          <FormField v-bind="fields.zipCode" />
          <FormField v-bind="fields.city" />
        </div>
        <FormField v-bind="fields.address" />
      </div>
      <div
        v-if="packageNames.length"
        class="contact-form-block">
        <h3 class="contact-form-block__title">
          {{ $t('contact.form.packagesTitle') }}
        </h3>
        <FormSelect
          v-bind="{ ...fields.package, items: packageNames.map((name) => ({ label: name, value: name })), selectedItem: selectedPackage }" />
      </div>
      <div class="contact-form-block">
        <h3 class="contact-form-block__title">
          {{ $t('contact.form.additionalCommentTitle') }}
        </h3>
        <FormField v-bind="fields.comment" />
      </div>
      <div class="contact-form-block">
        <FormCheckbox
          name="consentPrivacyPolicy"
          :checked-value="true"
          rules="required">
          <i18n-t
            keypath="contact.form.consentPrivacyPolicy"
            scope="global">
            <template #linkText>
              <NuxtLink
                :to="localePath({ name: 'documents-dslug', params: { dslug: getDocumentSlugForAlias('privacyPolicyContact') } })"
                target="_blank"
                class="text-magenta font-medium">
                {{ $t('contact.form.consentPrivacyPolicyLinkText') }}
              </NuxtLink>
            </template>
          </i18n-t>
          &nbsp;*
        </FormCheckbox>
        <FormCheckbox
          name="consentMarketing"
          :checked-value="true">
          {{ $t('contact.form.marketing') }}
        </FormCheckbox>
      </div>
      <div class="contact-form-block">
        <button
          class="contact-form-submit"
          @click="onSubmit">
          {{ $t(submitLabel) }}
        </button>
      </div>
    </form>
  </div>
</template>
